import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { unitPriceFor, tierFor, FREE_SHIP_THRESHOLD, SHIP_FEE } from "@/data/products";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mx_cart") || "[]") || []; }
    catch (e) { console.warn("cart restore failed", e); return []; }
  });
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState(null); // { code, kind, percent, amountOff, bundleQty, bundlePrice, label }
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null); // server-authoritative pricing

  // Persist cart across sessions/browser restarts
  useEffect(() => {
    try { localStorage.setItem("mx_cart", JSON.stringify(items)); }
    catch (e) { console.warn("cart persist failed (storage full/blocked)", e); }
  }, [items]);

  useEffect(() => {
    const saved = localStorage.getItem("mx_promo");
    if (saved) { try { setPromo(JSON.parse(saved)); } catch (e) { /* ignore bad json */ } }
  }, []);

  // Abandoned-cart capture: save the cart server-side when we know the shopper's email.
  useEffect(() => {
    const email = localStorage.getItem("mx_email");
    if (!email || items.length === 0) return;
    const t = setTimeout(() => {
      const payload = items.map((i) => {
        const unit = unitPriceFor(i.price, i.qty);
        return { id: i.id, name: i.name, size: i.size, qty: i.qty, unit, lineTotal: unit * i.qty };
      });
      const tot = payload.reduce((s, l) => s + l.lineTotal, 0);
      axios.post(`${API}/carts`, { email, items: payload, total: tot }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    trackEvent("add_to_cart", { product_id: product.id, product_name: product.name, qty });
    setItems((prev) => {
      const found = prev.find((i) => i.id === product.id);
      if (found) return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { ...product, qty }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((id) => setItems((p) => p.filter((i) => i.id !== id)), []);
  const updateQty = useCallback((id, qty) => {
    setItems((p) => (qty <= 0 ? p.filter((i) => i.id !== id) : p.map((i) => (i.id === id ? { ...i, qty } : i))));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const applyPromo = useCallback(async (raw, opts = {}) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) return false;
    try {
      const { data } = await axios.post(`${API}/validate-code`, { code });
      const p = { code, kind: data.kind || (data.amount_off ? "amount" : "percent"),
                  percent: data.discount_percent || 0, amountOff: data.amount_off || null,
                  bundleQty: data.bundle_qty || null, bundlePrice: data.bundle_price || null,
                  label: data.label || (data.amount_off ? `$${data.amount_off} off` : `${data.discount_percent}% off`) };
      setPromo(p);
      localStorage.setItem("mx_promo", JSON.stringify(p));
      if (!opts.silent) toast.success(`Code applied — ${p.label}`);
      return true;
    } catch (e) {
      if (!opts.silent) toast.error(e.response?.data?.detail || "Invalid code");
      return false;
    }
  }, []);

  const removePromo = useCallback(() => { setPromo(null); localStorage.removeItem("mx_promo"); }, []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const lines = items.map((i) => {
    const unit = unitPriceFor(i.price, i.qty);
    return { ...i, unit, lineTotal: unit * i.qty, saved: (i.price - unit) * i.qty, tier: tierFor(i.qty) };
  });
  const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
  const afterBulk = lines.reduce((s, l) => s + l.lineTotal, 0);
  const bulkSavings = subtotal - afterBulk;

  // Server-authoritative pricing: a live /price-quote wins over the local estimate,
  // so bundle deals, free-ship codes etc. always match what checkout charges.
  const sig = JSON.stringify([items.map((i) => [i.id, i.qty]), promo?.code || ""]);
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const payload = items.map((i) => {
          const unit = unitPriceFor(i.price, i.qty);
          return { id: i.id, name: i.name, size: i.size, qty: i.qty, unit, lineTotal: unit * i.qty };
        });
        const { data } = await axios.post(`${API}/price-quote`, { items: payload, promo_code: promo?.code || null });
        setQuote({ ...data, sig });
      } catch (e) {
        setQuote(null);
        const st = e.response?.status;
        if (promo && (st === 404 || st === 409)) {
          toast.error(e.response?.data?.detail || "Promo code no longer valid — removed");
          setPromo(null); localStorage.removeItem("mx_promo");
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = quote && quote.sig === sig ? quote : null;
  const localDiscount = promo ? (promo.amountOff ? Math.min(promo.amountOff, afterBulk) : Math.round(afterBulk * ((promo.percent || 0) / 100))) : 0;
  const promoDiscount = q ? q.promo_discount : localDiscount;
  const goods = q ? Math.max(0, Math.round((q.total - q.shipping_fee) * 100) / 100) : Math.max(0, afterBulk - localDiscount);
  const freeShip = q ? q.free_shipping : (goods >= FREE_SHIP_THRESHOLD || promo?.kind === "freeship");
  const shipping = q ? q.shipping_fee : (items.length === 0 || freeShip ? 0 : SHIP_FEE);
  const freeShipRemaining = q ? q.free_ship_remaining : (freeShip ? 0 : Math.max(0, FREE_SHIP_THRESHOLD - goods));
  const total = q ? q.total : Math.round((goods + shipping) * 100) / 100;
  const dealLabel = q?.deal_label || promo?.label || null;
  const bundleHint = q?.bundle_hint || null;

  const checkoutStripe = useCallback(async (shippingAddr, termsAccepted = false) => {
    if (!lines.length) return { ok: false };
    const email = (shippingAddr.email || localStorage.getItem("mx_email") || "").trim().toLowerCase() || null;
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        items: lines.map((l) => ({ id: l.id, name: l.name, size: l.size, qty: l.qty, unit: l.unit, lineTotal: l.lineTotal })),
        subtotal, bulk_savings: bulkSavings, promo_code: promo?.code || null, promo_discount: promoDiscount,
        shipping_fee: shipping, total, email, note: note.trim() || null, terms_accepted: termsAccepted,
        shipping: { name: shippingAddr.name, phone: shippingAddr.phone, line1: shippingAddr.line1, line2: shippingAddr.line2 || "", city: shippingAddr.city, state: shippingAddr.state, zip: shippingAddr.zip, country: "US" },
        origin_url: window.location.origin,
      });
      if (email) localStorage.setItem("mx_email", email);
      if (data.checkout_url) { window.location.href = data.checkout_url; return { ok: true }; }
      toast.error("Payment could not be started. Please try again or contact us on WhatsApp.");
      return { ok: false };
    } catch (e) {
      // FastAPI validation errors arrive as an array — never render raw objects (crashes React).
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x?.msg || "Invalid field").join(" · ")
        : (typeof d === "string" ? d : "Payment could not be processed. Please check your details or try an alternative payment method.");
      toast.error(msg);
      return { ok: false };
    }
  }, [lines, subtotal, bulkSavings, promo, promoDiscount, shipping, total, note]);

  return (
    <CartContext.Provider value={{ items, lines, open, setOpen, addItem, removeItem, updateQty, clear, count,
      subtotal, bulkSavings, promo, promoDiscount, shipping, freeShip, freeShipRemaining, total, dealLabel, bundleHint, note, setNote, applyPromo, removePromo, checkoutStripe }}>
      {children}
    </CartContext.Provider>
  );
};
